/**
 * Logging schemas for structured log entries.
 */

import { z } from 'zod';
import { SafeParseResult } from '../zod-utils';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { JsonObjectSchema } from './common.schemas';
import {
    type AuthenticatedLogEntry,
    type LogSeverity,
    type LogService,
    type LogException,
    type LogIngestRequest,
    type LogQueryRequest,
    type LogQueryResponse,
    type RequestLogEntry,
    type LogData,
    type LogDataValue,
    type LogDataUnsafe,
    type SambaImageTag,
    type GuidedCloudLoggingQueryRequest,
    type RawCloudLoggingQueryRequest,
    type CloudLoggingQueryRequest,
    type CloudLoggingEntry,
    type ListCloudLoggingEntriesPageResponse,
    type CloudLoggingOption,
    type CloudLoggingOptionSearchRequest,
    type ListCloudLoggingOptionsPageResponse,
    CLOUD_LOGGING_MAX_CURSOR_LENGTH,
    CLOUD_LOGGING_MAX_FILTER_LENGTH,
    CLOUD_LOGGING_MAX_PAGE_LIMIT,
    CLOUD_LOGGING_MAX_OPTION_TERMS,
    CLOUD_LOGGING_MAX_OPTION_TERM_LENGTH,
    FORBIDDEN_LOG_DATA_KEYS,
} from '../logging.types';

export const LogSeveritySchema: z.ZodType<LogSeverity> = z.union([
    z.literal('DEFAULT'),
    z.literal('INFO'),
    z.literal('NOTICE'),
    z.literal('WARNING'),
    z.literal('ERROR'),
    z.literal('DEBUG'),
    z.literal('CRITICAL'),
    z.literal('ALERT'),
    z.literal('EMERGENCY'),
]);

export const LogServiceSchema: z.ZodType<LogService> = z.union([
    z.literal('pillar-backend'),
    z.literal('pillar-frontend'),
    z.literal('samba-core'),
    z.literal('samba-website'),
    z.literal('samba-sidebar'),
]);

export const SambaImageTagSchema: z.ZodType<SambaImageTag> = z.string().min(1);

export const LogExceptionSchema: z.ZodType<LogException> = z.lazy(() =>
    z.object({
        name: z.string().min(1),
        message: z.string().min(1),
        stack: z.string().min(1).optional(),
        cause: LogExceptionSchema.optional(),
    })
);

const ForbiddenLogDataKeySet: ReadonlySet<string> = new Set(FORBIDDEN_LOG_DATA_KEYS);

const RawLogDataValueSchema: z.ZodType<LogDataValue> = z.lazy(() =>
    z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.array(RawLogDataValueSchema),
        z.record(z.string(), RawLogDataValueSchema),
    ])
);

const RawLogDataRecordSchema: z.ZodType<LogDataUnsafe> = z.record(
    z.string(),
    RawLogDataValueSchema
);

export const LogDataSchema: z.ZodType<LogData> = RawLogDataRecordSchema.superRefine(
    (value: LogDataUnsafe, ctx: z.RefinementCtx) => {
        collectForbiddenLogFields(value, ctx, []);
    }
);

type LogDataPath = Array<string | number>;

function collectForbiddenLogFields(
    value: LogDataValue,
    ctx: z.RefinementCtx,
    path: LogDataPath
): void {
    const arrayParse: SafeParseResult<LogDataValue[]> = z
        .array(RawLogDataValueSchema)
        .safeParse(value);
    if (arrayParse.success) {
        arrayParse.data.forEach((entry: LogDataValue, index: number) => {
            collectForbiddenLogFields(entry, ctx, [...path, index]);
        });
        return;
    }

    const recordParse: SafeParseResult<LogDataUnsafe> = RawLogDataRecordSchema.safeParse(value);
    if (recordParse.success && !Array.isArray(value)) {
        for (const key in recordParse.data) {
            if (!Object.prototype.hasOwnProperty.call(recordParse.data, key)) {
                continue;
            }
            const nextPath: LogDataPath = [...path, key];
            if (ForbiddenLogDataKeySet.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Forbidden log data field: ${key}`,
                    path: nextPath,
                });
            }
            const nextValueParse: SafeParseResult<LogDataValue> = RawLogDataValueSchema.safeParse(
                recordParse.data[key]
            );
            if (!nextValueParse.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid log data value',
                    path: nextPath,
                });
                continue;
            }
            collectForbiddenLogFields(nextValueParse.data, ctx, nextPath);
        }
    }
}

export const RequestLogEntrySchema: z.ZodType<RequestLogEntry> = z
    .object({
        client_timestamp: z.string().datetime().optional(),

        severity: LogSeveritySchema,
        service: LogServiceSchema,
        topics: z.array(z.string().min(1)),
        subsystem: z.string().min(1).optional(),
        workflow_id: z.string().min(1).optional(),
        website_id: z.string().min(1).optional(),
        operation_id: z.string().min(1),
        correlation_id: z.string().min(1).optional(),
        trace_id: z.string().min(1).optional(),
        samba_image_tag: SambaImageTagSchema.optional(),

        customer_id: z.string().min(1).optional(),

        message: z.string().min(1),
        data: LogDataSchema.optional(),
        exception: LogExceptionSchema.optional(),
        stack_trace: z.string().min(1).optional(),
    })
    .strip();

export const AuthenticatedLogEntrySchema: z.ZodType<AuthenticatedLogEntry> = z.object({
    timestamp: z.string().datetime(),
    client_timestamp: z.string().datetime().optional(),

    severity: LogSeveritySchema,
    service: LogServiceSchema,
    topics: z.array(z.string().min(1)),
    subsystem: z.string().min(1).optional(),
    workflow_id: z.string().min(1).optional(),
    website_id: z.string().min(1).optional(),
    operation_id: z.string().min(1),
    correlation_id: z.string().min(1).optional(),
    trace_id: z.string().min(1).optional(),
    samba_image_tag: SambaImageTagSchema.optional(),

    user_id: z.string().min(1).optional(),
    role_assignment_id: z.string().min(1).optional(),
    audit_id: z.string().min(1).optional(),
    organization_id: z.string().min(1).optional(),
    customer_id: z.string().min(1).optional(),

    message: z.string().min(1),
    data: LogDataSchema.optional(),
    exception: LogExceptionSchema.optional(),
    stack_trace: z.string().min(1).optional(),
});

export const LogIngestRequestSchema: z.ZodType<LogIngestRequest> = z.object({
    entries: z.array(RequestLogEntrySchema),
});

export const LogQueryRequestSchema: z.ZodType<LogQueryRequest> = z.object({
    user_id: z.string().min(1).optional(),
    audit_id: z.string().min(1).optional(),
    role_assignment_id: z.string().min(1).optional(),
    organization_id: z.string().min(1).optional(),
    customer_id: z.string().min(1).optional(),
    trace_id: z.string().min(1).optional(),
    from_timestamp: z.string().datetime().optional(),
    to_timestamp: z.string().datetime().optional(),
    limit: z.coerce.number().int().positive().optional(),
});

export const LogQueryResponseSchema: z.ZodType<LogQueryResponse> = z.object({
    entries: z.array(AuthenticatedLogEntrySchema),
});

export const GuidedCloudLoggingQueryRequestSchema = z
    .object({
        mode: z.literal('guided'),
        start_timestamp: z.string().datetime(),
        end_timestamp: z.string().datetime(),
        text: z.string().optional(),
        severities: z.array(LogSeveritySchema).optional(),
        resource_types: z.array(z.string().min(1)).optional(),
        log_names: z.array(z.string().min(1)).optional(),
        trace_id: z.string().optional(),
        limit: z.number().int().positive().max(CLOUD_LOGGING_MAX_PAGE_LIMIT).optional(),
        cursor: z.string().min(1).max(CLOUD_LOGGING_MAX_CURSOR_LENGTH).optional(),
    })
    .strict()
    .superRefine((request: GuidedCloudLoggingQueryRequest, ctx: z.RefinementCtx): void => {
        if (Date.parse(request.start_timestamp) > Date.parse(request.end_timestamp)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Start timestamp must not be after end timestamp',
                path: ['end_timestamp'],
            });
        }
    }) satisfies z.ZodType<GuidedCloudLoggingQueryRequest>;

export const RawCloudLoggingQueryRequestSchema = z
    .object({
        mode: z.literal('raw'),
        filter: z.string().max(CLOUD_LOGGING_MAX_FILTER_LENGTH),
        limit: z.number().int().positive().max(CLOUD_LOGGING_MAX_PAGE_LIMIT).optional(),
        cursor: z.string().min(1).max(CLOUD_LOGGING_MAX_CURSOR_LENGTH).optional(),
    })
    .strict() satisfies z.ZodType<RawCloudLoggingQueryRequest>;

export const CloudLoggingQueryRequestSchema = z.discriminatedUnion('mode', [
    GuidedCloudLoggingQueryRequestSchema,
    RawCloudLoggingQueryRequestSchema,
]) satisfies z.ZodType<CloudLoggingQueryRequest>;

export const CloudLoggingEntrySchema: z.ZodType<CloudLoggingEntry> = z
    .object({
        timestamp: z.string().datetime().optional(),
        severity: LogSeveritySchema.optional(),
        log_id: z.string().min(1).optional(),
        resource_type: z.string().min(1).optional(),
        trace: z.string().min(1).optional(),
        insert_id: z.string().min(1).optional(),
        details: JsonObjectSchema,
    })
    .strict();

export const ListCloudLoggingEntriesPageResponseSchema: z.ZodType<ListCloudLoggingEntriesPageResponse> =
    z
        .object({
            entries: z.array(CloudLoggingEntrySchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const CloudLoggingOptionSearchRequestSchema: z.ZodType<CloudLoggingOptionSearchRequest> = z
    .object({
        terms: z
            .array(z.string().trim().min(1).max(CLOUD_LOGGING_MAX_OPTION_TERM_LENGTH))
            .max(CLOUD_LOGGING_MAX_OPTION_TERMS)
            .optional(),
        limit: z.number().int().positive().max(CLOUD_LOGGING_MAX_PAGE_LIMIT).optional(),
        cursor: z.string().min(1).max(CLOUD_LOGGING_MAX_CURSOR_LENGTH).optional(),
    })
    .strict();

export const CloudLoggingOptionSchema: z.ZodType<CloudLoggingOption> = z
    .object({
        value: z.string().min(1),
        label: z.string().min(1),
    })
    .strict();

export const ListCloudLoggingOptionsPageResponseSchema: z.ZodType<ListCloudLoggingOptionsPageResponse> =
    z
        .object({
            options: z.array(CloudLoggingOptionSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();
